/* A-Champs 2026 · Motion layer (GSAP 3 + ScrollTrigger + SplitText, all free for commercial use).
   One authored moment per page: the signal travels, then the conclusion lands.
   - Resolution panel: the beam draws and the key line lights up word by word, tied to scroll.
   - Perceive → Decide → Move (flow style): the signal dot travels the line as you read it.
   - Workflow track: the line fills as the steps come into view.
   - Opening hero: the media settles on load and drifts slightly on scroll-out.
   Everything is fully visible without this file. Libraries load only when the page has one of
   these elements AND the visitor has not asked for reduced motion. */
(() => {
  const cfg = window.acMotion || {};
  if ( window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const SEL = '[data-ac-resolve], .ac-model--flow, .ac-steps--track, .ac-hero--lead';
  if (!document.querySelector(SEL)) return;

  const load = (src) => new Promise((ok, fail) => {
    const s = document.createElement('script'); s.src = src; s.async = false; s.onload = ok; s.onerror = fail; document.head.appendChild(s);
  });

  const contexts = new Map();

  const build = (root) => {
    const { gsap, ScrollTrigger, SplitText } = window;
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      /* 1 · Resolution panel: beam + word-by-word light-up, scrubbed */
      root.querySelectorAll('[data-ac-resolve]').forEach((panel) => {
        const beam = panel.querySelector('.ac-resolve__beam');
        const dot = beam && beam.querySelector('i');
        const text = panel.querySelector('.ac-resolve__text');
        if (!beam || !text) return;
        const fill = document.createElement('span');
        fill.className = 'ac-resolve__fill'; beam.appendChild(fill);
        const split = SplitText.create(text, { type: 'words', wordsClass: 'ac-resolve__word' });
        const accent = [...text.querySelectorAll('em .ac-resolve__word, i .ac-resolve__word')];
        const tl = gsap.timeline({
          defaults: { ease: 'none' },
          scrollTrigger: { trigger: panel, start: 'top 82%', end: 'center 52%', scrub: 0.6 }
        });
        tl.fromTo(fill, { scaleX: 0 }, { scaleX: 1, duration: 1 }, 0)
          .fromTo(dot, { x: 0 }, { x: () => beam.clientWidth - dot.offsetWidth, duration: 1 }, 0)
          .fromTo(split.words, { opacity: 0.18 }, { opacity: 1, stagger: 0.06, duration: 0.5 }, 0.15);
        if (accent.length) tl.fromTo(accent, { color: '#ffffff' }, { color: '#FF4000', duration: 0.35, stagger: 0.04 }, '>-0.2');
      });

      mm.add('(min-width: 750px)', () => {
        /* 2 · Flow model: the signal dot travels Perceive → Decide → Move */
        root.querySelectorAll('.ac-model--flow').forEach((model) => {
          const dot = document.createElement('span');
          dot.className = 'ac-flow-dot'; model.appendChild(dot);
          const stages = [...model.querySelectorAll(':scope > div')];
          const tl = gsap.timeline({ defaults: { ease: 'none' }, scrollTrigger: { trigger: model, start: 'top 78%', end: 'bottom 48%', scrub: 0.5 } });
          tl.fromTo(dot, { x: 0 }, { x: () => model.clientWidth - dot.offsetWidth, duration: 1 }, 0)
            .fromTo(stages.slice(1), { opacity: 0.32 }, { opacity: 1, duration: 0.25, stagger: 0.38 }, 0.2);
          return () => dot.remove();
        });

        /* 3 · Workflow track: the line fills, each dot lights as the line reaches it */
        root.querySelectorAll('.ac-steps--track').forEach((track) => {
          const fill = document.createElement('span');
          fill.className = 'ac-track-fill'; track.appendChild(fill);
          const steps = [...track.querySelectorAll('.ac-step')];
          const last = Math.max(steps.length - 1, 1);
          gsap.fromTo(fill, { scaleX: 0 }, {
            scaleX: 1, ease: 'none',
            scrollTrigger: {
              trigger: track, start: 'top 80%', end: 'bottom 55%', scrub: 0.5,
              onUpdate: (self) => steps.forEach((s, i) => s.classList.toggle('is-lit', self.progress >= i / last - 0.02))
            }
          });
          return () => fill.remove();
        });
      });

      /* 4 · Opening hero: media settles on load (text untouched, so LCP is not delayed), drifts on scroll-out */
      root.querySelectorAll('.ac-hero--lead .ac-hero__media').forEach((media) => {
        const inner = media.querySelector('.ac-media__el, video, img, svg');
        if (!inner) return;
        gsap.fromTo(inner, { scale: 1.06 }, { scale: 1, duration: 1.6, ease: 'expo.out' });
        mm.add('(min-width: 750px)', () => {
          gsap.to(inner, { yPercent: 8, ease: 'none', scrollTrigger: { trigger: media.closest('.ac-hero'), start: 'top top', end: 'bottom top', scrub: true } });
        });
      });
    }, root);
    contexts.set(root, ctx);
  };

  const start = () => {
    const { gsap, ScrollTrigger, SplitText } = window;
    gsap.registerPlugin(ScrollTrigger, SplitText);
    document.documentElement.classList.add('ac-gsap');
    build(document);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => ScrollTrigger.refresh());
    /* Theme editor: rebuild a section when it is re-rendered, clean up when it is removed */
    document.addEventListener('shopify:section:unload', (e) => { const c = contexts.get(e.target); if (c) { c.revert(); contexts.delete(e.target); } });
    document.addEventListener('shopify:section:load', (e) => { build(e.target); ScrollTrigger.refresh(); });
  };

  if (window.gsap && window.ScrollTrigger && window.SplitText) { start(); return; }
  load(cfg.gsap).then(() => Promise.all([load(cfg.scrollTrigger), load(cfg.splitText)])).then(start).catch(() => {});
})();
